import React, { useState, useRef, useEffect } from "react";
import { ChevronDown, X, Check } from "lucide-react";

const MultiSelect = ({
  options,
  value,
  onChange,
  placeholder = "Select options...",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  // Handle outside click
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const toggleOption = (optionValue) => {
    const newValue = [...value];
    if (newValue.includes(optionValue)) {
      newValue.splice(newValue.indexOf(optionValue), 1);
    } else {
      newValue.push(optionValue);
    }
    onChange(newValue);
  };

  const removeOption = (e, optionValue) => {
    e.stopPropagation();
    const newValue = value.filter((v) => v !== optionValue);
    onChange(newValue);
  };

  const getLabel = (val) => {
    const opt = options.find((o) =>
      typeof o === "string" ? o === val : o.value === val,
    );
    return opt ? (typeof opt === "string" ? opt : opt.label) : val;
  };

  return (
    <div className="relative font-poppins w-full" ref={containerRef}>
      <div
        className="min-h-[42px] w-full p-2 border border-gray-300 rounded-lg dark:bg-gray-800 dark:border-gray-700 dark:text-white flex items-center justify-between cursor-pointer focus-within:ring-2 focus-within:ring-blue-500 transition-colors"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex flex-wrap gap-1 items-center flex-1">
          {value.length === 0 ? (
            <span className="text-gray-400 dark:text-gray-500 ml-1">
              {placeholder}
            </span>
          ) : (
            value.map((v) => (
              <span
                key={v}
                className="flex items-center gap-1 bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300 px-2 py-0.5 rounded text-sm transition-colors"
              >
                {getLabel(v)}
                <button
                  onClick={(e) => removeOption(e, v)}
                  className="hover:bg-blue-200 dark:hover:bg-blue-800 rounded-full p-0.5 transition-colors"
                >
                  <X size={12} />
                </button>
              </span>
            ))
          )}
        </div>
        <div className="text-gray-400 ml-2">
          <ChevronDown
            size={16}
            className={`transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
          />
        </div>
      </div>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl max-h-60 overflow-y-auto custom-scrollbar">
          {options.map((option, idx) => {
            const val = typeof option === "string" ? option : option.value;
            const label = typeof option === "string" ? option : option.label;
            const isSelected = value.includes(val);

            return (
              <div
                key={idx}
                onClick={() => toggleOption(val)}
                className="flex items-center px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors"
              >
                <div
                  className={`w-4 h-4 rounded border flex items-center justify-center mr-3 ${isSelected ? "bg-blue-600 border-blue-600 text-white" : "border-gray-300 dark:border-gray-600"}`}
                >
                  {isSelected && <Check size={12} strokeWidth={3} />}
                </div>
                <span className="text-sm text-gray-700 dark:text-gray-200">
                  {label}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MultiSelect;
